import PropTypes from 'prop-types'
import { useState } from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import ReCAPTCHA from 'react-google-recaptcha'
import { prop } from 'ramda'

import {
  Box,
  Button,
  Dialog,
  Heading,
  Icon,
  Link,
  LoadingOverlay,
  Text,
} from '@redriverpress/system'

import { validateFieldErrors, formatErrors } from 'Common/utils/formik'
import AutoScrollAlert from 'Common/components/alert/AutoScrollAlert'
import Field from 'Common/components/forms/Field'
import CheckboxField from 'Common/components/forms/CheckboxField'
import SelectField from 'Common/components/forms/SelectField'
import { getCountryOptions } from 'Common/services/country'
import signUp from 'Common/services/signUp'

import { PRICING_PATH } from 'Common/constants/redirect'
import useRecaptcha from 'Common/hooks/useRecaptcha'

const SCHEMA = Yup.object({
  first_name: Yup.string().required('Please enter your first name'),
  last_name: Yup.string().required('Please enter your last name'),
  email: Yup.string()
    .email('Please enter a valid email')
    .required('Please enter a valid email'),
  password: Yup.string().required('Please enter your password'),
  school_name: Yup.string(),
  country: Yup.string().required('Please select your country'),
  terms: Yup.boolean().oneOf([true], "You must agree to Ellii's\xa0terms"),
  subscribed_to_newsletter: Yup.boolean(),
  commit: Yup.string(),
})

const INITIAL_VALUES = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  school_name: '',
  country: '',
  terms: false,
  subscribed_to_newsletter: true,
  commit: '',
}

// Use the default values as errors. If value is falsy then the field has an error.
const INITIAL_ERRORS = INITIAL_VALUES

export default function CreateFreeAccountDialog({
  pollId,
  pollTopic,
  open,
  onOpenChange,
}) {
  const recaptcha = useRecaptcha()
  const [remoteErrors, setRemoteErrors] = useState([])
  const [alert, setAlert] = useState()
  const [isPending, setIsPending] = useState(false)

  const countries = getCountryOptions({ emptyName: 'Select...' })

  const validateRemoteErrors = validateFieldErrors(remoteErrors)

  const handleSubmit = async (formValues, { setFieldError }) => {
    setIsPending(true)

    const context = {
      variation: 'free-polls',
      interactive_set_id: pollId,
      interactive_set_topic: pollTopic,
    }
    const { commit, ...user } = formValues
    const recaptchaToken = await recaptcha.executeAsync()

    await signUp({ user, commit, recaptchaToken, context })
      .then(() => {
        setIsPending(false)
        onOpenChange({ isOpen: false, signupSuccess: true })
      })
      .catch(err => {
        // Each key is a form field. Each value is an array of messages
        const fieldErrors = err.fieldErrors || []

        setAlert(err)

        const errorFields = formatErrors(fieldErrors, formValues)

        // Store errors. This will be used to validate fields on onChange/onBlur
        setRemoteErrors(errorFields)

        // Render errors right now
        errorFields.forEach(({ field, message: errMsg }) => {
          setFieldError(field, errMsg)
        })
        setIsPending(false)
      })
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.StyledContent
          onPointerDownOutside={e => {
            e.preventDefault()
          }}
        >
          <Dialog.StyledTitle>
            <Heading as='h6' mb={0}>
              Sign Up
            </Heading>
          </Dialog.StyledTitle>

          {isPending && <LoadingOverlay />}

          <Text my={4}>
            Ellii Polls are a <strong>free&nbsp;feature</strong>. You can access
            Ellii&nbsp;Polls by creating an account or{' '}
            <Link
              color='black'
              hover='blue700'
              href={`/sign_in?return_to=${window.location.href}`}
            >
              logging&nbsp;in
            </Link>
            . You will need a paid subscription to enjoy all other
            Ellii&nbsp;materials. To view our pricing plans,{' '}
            <Link color='black' hover='blue700' href={PRICING_PATH}>
              click&nbsp;here
            </Link>
            .
          </Text>

          {alert && (
            <AutoScrollAlert
              alert={alert}
              fallback='Sign-up could not be completed. Please confirm your details and try&nbsp;again.'
            />
          )}

          <Formik
            initialValues={INITIAL_VALUES}
            initialErrors={INITIAL_ERRORS}
            validate={validateRemoteErrors}
            validationSchema={SCHEMA}
            onSubmit={handleSubmit}
          >
            {({ isValid, submitForm, setFieldValue }) => (
              <Form>
                <Box display={['block', null, 'flex']}>
                  <Box mr={[0, null, 2]}>
                    <Field
                      name='first_name'
                      label='First name*'
                      placeholder='Your first name'
                      icon='user'
                    />
                  </Box>
                  <Box ml={[0, null, 2]}>
                    <Field
                      name='last_name'
                      label='Last name*'
                      placeholder='Your last name'
                      icon='user'
                    />
                  </Box>
                </Box>
                <Field
                  name='email'
                  label='Email*'
                  placeholder='you@example.com'
                  icon='envelope'
                  sanitizeError={false}
                />
                <Field
                  name='password'
                  type='password'
                  label='Password*'
                  placeholder='Create a password'
                  icon='key-skeleton'
                />
                <Box display={['block', null, 'flex']}>
                  <Box mr={[0, null, 2]} width={[1, null, 1 / 2]}>
                    <Field
                      name='school_name'
                      label='Organization (optional)'
                      placeholder='Your organization'
                      icon='building'
                    />
                  </Box>
                  <Box ml={[0, null, 2]} width={[1, null, 1 / 2]}>
                    <SelectField
                      name='country'
                      label='Country*'
                      icon='globe'
                      options={countries}
                      value={{ code: '' }}
                      getOptionText={prop('name')}
                      getOptionValue={prop('code')}
                    />
                  </Box>
                </Box>

                <Box my={3}>
                  <CheckboxField
                    name='terms'
                    label={
                      <Text fontSize={0}>
                        I agree to Elli’s{' '}
                        <Link
                          color='black'
                          hover='blue700'
                          href='/terms'
                          target='_blank'
                        >
                          terms
                        </Link>{' '}
                        and{' '}
                        <Link
                          color='black'
                          hover='blue700'
                          href='/privacy'
                          target='_blank'
                        >
                          privacy&nbsp;policy
                        </Link>
                        .
                      </Text>
                    }
                    labelPadding={3}
                    small
                  />
                </Box>

                {/* Captcha is here, baby! */}
                <ReCAPTCHA {...recaptcha.props} size='invisible' />

                <Box textAlign='center'>
                  <Button
                    disabled={isPending || !isValid}
                    onClick={() => {
                      setFieldValue('commit', '', false)
                      submitForm()
                    }}
                  >
                    Create account
                    <Icon icon='arrow-right' pl={2} />
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
          <Box
            my={3}
            width='1'
            display='flex'
            flexDirection='column'
            textAlign='center'
          >
            <Text>
              Have an account?{' '}
              <Link
                color='black'
                hover='blue700'
                href={`/sign_in?return_to=${window.location.href}`}
              >
                <strong>Log&nbsp;in</strong>
              </Link>
            </Text>
          </Box>
        </Dialog.StyledContent>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

CreateFreeAccountDialog.propTypes = {
  pollId: PropTypes.number.isRequired,
  pollTopic: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
}
